import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaCheckCircle, FaEye, FaEyeSlash, FaKey, FaShieldAlt } from "react-icons/fa";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import Seo from "../components/Seo";

const minimumPasswordLength = 12;

export default function SetPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const activationToken = useMemo(() => {
    const parameters = new URLSearchParams(location.search);
    return parameters.get("token") || parameters.get("activationToken") || "";
  }, [location.search]);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (!activationToken) {
      setError("This activation link is missing its security token. Please request a new activation email.");
      return;
    }
    if (password.length < minimumPasswordLength) {
      setError(`Use at least ${minimumPasswordLength} characters for your password.`);
      return;
    }
    if (password !== confirmation) {
      setError("The passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(
        API_PATHS.auth.employeeActivationSetPassword,
        { token: activationToken, password },
        { withCredentials: false },
      );
      setPassword("");
      setConfirmation("");
      setCompleted(true);
    } catch (requestError) {
      setError(requestError?.message || "We could not set your password. The activation link may have expired.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (completed) {
    return (
      <div className="login-experience min-h-screen px-4 py-5 text-slate-900 sm:px-6 sm:py-8">
        <Seo title="Password Set | ClassNX" description="Your ClassNX employee password has been set." canonical="/set-password" robots="noindex, nofollow" />
        <div className="mx-auto flex min-h-[calc(100vh-2.5rem)] max-w-xl items-center justify-center sm:min-h-[calc(100vh-4rem)]">
          <main className="login-shell w-full p-7 sm:p-10">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-2xl text-emerald-600"><FaCheckCircle /></div>
            <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Account activated</p>
            <h1 className="mt-3 text-3xl font-semibold text-slate-900">Your password is ready</h1>
            <p className="mt-4 text-sm leading-6 text-slate-600">Your employee account has been activated. Sign in with your employee ID and the password you just created.</p>
            <Link to="/login" className="login-submit mt-8 inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold text-white"><FaKey /> Go to sign in</Link>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="login-experience min-h-screen px-4 py-5 text-slate-900 sm:px-6 sm:py-8">
      <Seo title="Set Your Password | ClassNX" description="Activate your ClassNX employee account." canonical="/set-password" robots="noindex, nofollow" />
      <div className="mx-auto flex min-h-[calc(100vh-2.5rem)] max-w-xl items-center justify-center sm:min-h-[calc(100vh-4rem)]">
        <main className="login-shell w-full p-7 sm:p-10">
          <div className="flex items-center justify-between gap-4"><div className="flex items-center gap-3"><img className="login-logo" src={`${process.env.PUBLIC_URL}/logo.png`} alt="ClassNX" /><span className="text-sm font-bold tracking-[0.18em] text-slate-800">CLASSNX</span></div><div className="login-security-icon"><FaShieldAlt /></div></div>
          <p className="mt-10 text-xs font-bold uppercase tracking-[0.18em] text-teal-700">Employee activation</p>
          <h1 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl" style={{ fontFamily: "\"Merriweather\", serif" }}>Set your password</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">Create a password to activate your ClassNX employee account.</p>
          {error && <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-5 text-rose-800" role="alert">{error}</div>}
          <form className="mt-7 space-y-5" onSubmit={submit} noValidate>
            <label className="block text-sm font-semibold text-slate-700">New password
              <span className="relative mt-2 block"><input className="login-input w-full border px-4 py-3 pr-12 text-sm text-slate-800 outline-none" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" minLength={minimumPasswordLength} required disabled={isSubmitting} /><button className="absolute inset-y-0 right-3 text-sm text-teal-700" type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <FaEyeSlash /> : <FaEye />}</button></span>
            </label>
            <label className="block text-sm font-semibold text-slate-700">Confirm password<input className="login-input mt-2 w-full border px-4 py-3 text-sm text-slate-800 outline-none" type={showPassword ? "text" : "password"} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" minLength={minimumPasswordLength} required disabled={isSubmitting} /></label>
            <p className="text-xs leading-5 text-slate-500">Use at least {minimumPasswordLength} characters. The activation link can be used once.</p>
            <button type="submit" disabled={isSubmitting || !activationToken} className="login-submit inline-flex w-full items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70"><FaKey /> {isSubmitting ? "Setting password..." : "Set password"}</button>
          </form>
          <p className="mt-7 text-center text-sm text-slate-500">Already activated? <Link to="/login" className="font-semibold text-teal-700 hover:text-teal-800">Sign in</Link></p>
        </main>
      </div>
    </div>
  );
}
